import { LitElement, html, css } from 'lit';
import { fetchSearchResults } from '../lib/api.js';
import { router } from '../lib/router.js';
import { browserUrl, withBuildPrefix } from '../lib/build-paths.js';

const config = typeof __SITE_CONFIG__.features?.search === 'object' ? __SITE_CONFIG__.features.search : {};

export class SiteSearchPage extends LitElement {
  static properties = {
    _query: { state: true },
    _page: { state: true },
    _data: { state: true },
    _loading: { state: true },
    _error: { state: true },
  };

  static styles = css`
    :host { display: block; color: var(--color-text-primary); background: var(--color-bg-primary); }
    .search-page { width: min(900px, calc(100% - 2 * var(--content-padding, 2rem))); min-height: 65vh; margin: 0 auto; padding: clamp(3rem, 8vw, 6rem) 0; }
    h1 { margin: 0 0 .75rem; font-size: clamp(2.25rem, 6vw, 4rem); line-height: 1.05; }
    .intro { margin: 0 0 2rem; color: var(--color-text-secondary); font-size: var(--text-lg, 1.125rem); }
    form { position: relative; margin-bottom: 2rem; }
    input {
      width: 100%; min-height: 3.75rem; box-sizing: border-box; padding: .85rem 4.5rem .85rem 1.25rem;
      border: 1px solid var(--color-border); border-radius: 999px; color: var(--color-text-primary);
      background: var(--color-bg-card); font: inherit; font-size: 1rem; box-shadow: 0 12px 35px color-mix(in srgb, var(--color-text-primary) 7%, transparent);
    }
    input:focus { border-color: var(--color-accent); outline: 3px solid color-mix(in srgb, var(--color-accent) 22%, transparent); }
    form button { position: absolute; top: .45rem; right: .45rem; display: grid; width: 2.85rem; height: 2.85rem; place-items: center; border: 0; border-radius: 50%; color: #fff; background: var(--color-accent); cursor: pointer; }
    form button:hover { background: var(--color-accent-hover); }
    form button:focus-visible, .pager a:focus-visible, .result:focus-visible { outline: 3px solid color-mix(in srgb, var(--color-accent) 45%, transparent); outline-offset: 3px; }
    .status { min-height: 1.5rem; margin: 0 0 1rem; color: var(--color-text-secondary); }
    .results { display: grid; gap: .8rem; margin: 0; padding: 0; list-style: none; }
    .result { display: block; padding: 1.25rem 1.35rem; border: 1px solid var(--color-border); border-radius: var(--border-radius-xl, 1rem); color: inherit; background: var(--color-bg-card); text-decoration: none; transition: transform var(--transition-fast), border-color var(--transition-fast), box-shadow var(--transition-fast); }
    .result:hover { border-color: var(--color-border-hover); transform: translateY(-2px); box-shadow: 0 12px 30px color-mix(in srgb, var(--color-text-primary) 8%, transparent); }
    .eyebrow { display: flex; gap: .5rem; align-items: center; margin-bottom: .4rem; color: var(--color-text-muted); font-size: var(--text-xs, .75rem); font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
    .kind { color: var(--color-accent); }
    h2 { margin: 0; font-size: var(--text-xl, 1.25rem); }
    .excerpt { margin: .45rem 0 0; color: var(--color-text-secondary); line-height: 1.6; }
    .empty, .error { padding: 2rem; border-radius: var(--border-radius-xl, 1rem); color: var(--color-text-secondary); background: var(--color-bg-secondary); text-align: center; }
    .error { color: var(--color-accent); }
    .pager { display: flex; justify-content: center; gap: .75rem; margin-top: 2rem; }
    .pager a { min-width: 2.75rem; min-height: 2.75rem; display: grid; place-items: center; padding: 0 .8rem; border: 1px solid var(--color-border); border-radius: 999px; color: var(--color-text-primary); background: var(--color-bg-card); text-decoration: none; font-weight: 700; }
    @media (max-width: 560px) { .search-page { padding-top: 2.5rem; } .result { padding: 1rem; } }
  `;

  constructor() {
    super();
    this._query = '';
    this._page = 1;
    this._data = null;
    this._loading = false;
    this._error = '';
    this._request = 0;
    this._previousTitle = document.title;
    this._previousRobots = null;
    this._onPopState = () => this._syncLocation();
  }

  connectedCallback() {
    super.connectedCallback();
    const robots = document.head.querySelector('meta[name="robots"]');
    this._previousRobots = robots?.getAttribute('content') ?? null;
    const meta = robots || document.head.appendChild(document.createElement('meta'));
    meta.setAttribute('name', 'robots');
    meta.setAttribute('content', 'noindex,follow');
    document.title = `${config.heading || config.label || 'Search'} - ${__SITE_CONFIG__.name}`;
    window.addEventListener('popstate', this._onPopState);
    this._syncLocation();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this._onPopState);
    document.title = this._previousTitle;
    const robots = document.head.querySelector('meta[name="robots"]');
    if (robots && this._previousRobots === null) robots.remove();
    else if (robots) robots.setAttribute('content', this._previousRobots);
  }

  _syncLocation() {
    const params = new URLSearchParams(window.location.search);
    this._query = params.get('q') || '';
    this._page = Math.max(1, Number.parseInt(params.get('page') || '1', 10) || 1);
    this._load();
  }

  async _load() {
    const request = ++this._request;
    this._error = '';
    if (!this._query.trim()) {
      this._data = null;
      this._loading = false;
      await this.updateComplete;
      this.renderRoot.querySelector('input')?.focus({ preventScroll: true });
      return;
    }
    this._loading = true;
    try {
      const data = await fetchSearchResults(this._query, this._page);
      if (request !== this._request) return;
      this._data = data;
      this._page = data.page;
    } catch (error) {
      if (request !== this._request) return;
      this._error = error.message || config.error_text || 'Search unavailable.';
      this._data = null;
    } finally {
      if (request === this._request) {
        this._loading = false;
        await this.updateComplete;
        const key = history.state?.siteSearchFocus;
        if (key) this.renderRoot.querySelectorAll('[data-result-key]').forEach((link) => {
          if (link.dataset.resultKey === key) link.focus();
        });
      }
    }
  }

  _submit(event) {
    event.preventDefault();
    const query = new FormData(event.currentTarget).get('q')?.toString().trim() || '';
    this._go(query, 1);
  }

  _go(query, page) {
    const route = config.route || 'search';
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (page > 1) params.set('page', String(page));
    const logical = router.isDraft ? `/drafts${router.pathFor(route)}` : router.pathFor(route);
    const url = `${withBuildPrefix(logical)}${params.size ? `?${params}` : ''}`;
    history.pushState({}, '', url);
    router.refresh();
    this._syncLocation();
  }

  _href(result) {
    if (result.target_url) return browserUrl(result.target_url);
    const publicPath = router.pathFor(result.page_slug);
    const logical = router.isDraft ? `/drafts${publicPath}` : publicPath;
    const fragment = result.item_id || (result.kind !== 'page' ? result.element_id : '');
    return `${withBuildPrefix(logical)}${fragment ? `#${encodeURIComponent(fragment)}` : ''}`;
  }

  _remember(result) {
    history.replaceState({ ...history.state, siteSearchFocus: result.key }, '', location.href);
  }

  _kind(result) {
    const labels = config.result_labels || {};
    return labels[result.kind] || result.kind.replace('_', ' ');
  }

  _pageHref(page) {
    const params = new URLSearchParams({ q: this._query });
    if (page > 1) params.set('page', String(page));
    const route = config.route || 'search';
    const logical = router.isDraft ? `/drafts${router.pathFor(route)}` : router.pathFor(route);
    return `${withBuildPrefix(logical)}?${params}`;
  }

  render() {
    const total = this._data?.total || 0;
    const pages = Math.max(1, Math.ceil(total / (this._data?.page_size || 12)));
    const status = this._loading
      ? (config.loading_text || 'Searching…')
      : (this._data ? (config.count_text || '{count} results').replace('{count}', total) : '');
    return html`<main class="search-page">
      <h1>${config.heading || config.label || 'Search'}</h1>
      ${config.intro ? html`<p class="intro">${config.intro}</p>` : ''}
      <form role="search" @submit=${this._submit}>
        <input type="search" name="q" .value=${this._query} maxlength="100"
          placeholder=${config.placeholder || 'Search the site'} aria-label=${config.label || 'Search'}>
        <button type="submit" aria-label=${config.submit_label || config.label || 'Search'}>
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
        </button>
      </form>
      <p class="status" role="status" aria-live="polite">${status}</p>
      ${this._error ? html`<div class="error" role="alert">${config.error_text || this._error}</div>` : ''}
      ${this._data && !this._loading && total === 0 ? html`<div class="empty">${config.empty_text || 'No results found.'}</div>` : ''}
      ${this._data?.results?.length ? html`<ol class="results">
        ${this._data.results.map((result) => html`<li><a class="result" href=${this._href(result)}
          data-result-key=${result.key} @click=${() => this._remember(result)}>
          <div class="eyebrow"><span class="kind">${this._kind(result)}</span><span>·</span><span>${result.page_title}</span></div>
          <h2>${result.title}</h2>${result.excerpt ? html`<p class="excerpt">${result.excerpt}</p>` : ''}
        </a></li>`)}
      </ol>` : ''}
      ${this._data && pages > 1 ? html`<nav class="pager" aria-label=${config.pagination_label || 'Search result pages'}>
        ${this._page > 1 ? html`<a href=${this._pageHref(this._page - 1)} @click=${(e) => { e.preventDefault(); this._go(this._query, this._page - 1); }}>${config.previous_label || 'Previous'}</a>` : ''}
        ${this._page < pages ? html`<a href=${this._pageHref(this._page + 1)} @click=${(e) => { e.preventDefault(); this._go(this._query, this._page + 1); }}>${config.next_label || 'Next'}</a>` : ''}
      </nav>` : ''}
    </main>`;
  }
}

customElements.define('site-search-page', SiteSearchPage);
